import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { C, S } from '../../utils/styles';
import Icon from '../Icon';
import { useToast } from '../Toast';
import { renderMarkdown } from '../admin/learning/markdown';
import { getMyCourse, startCourse, completeLesson, submitQuiz } from './learnerApi';

// ----------------------------------------------------------------------------
// CoursePlayer — learner UI for a single course
// Shows the lesson sidebar + content + final quiz + result screen.
// ----------------------------------------------------------------------------

export default function CoursePlayer({ courseId, onBack, onCertificateIssued }) {
  const showToast = useToast();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeLessonIdx, setActiveLessonIdx] = useState(0);
  const [view, setView] = useState('lesson'); // 'lesson' | 'quiz' | 'result'
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMyCourse(courseId);
      setCourse(data);
      if (!data.attempt) {
        await startCourse(courseId);
        const fresh = await getMyCourse(courseId);
        setCourse(fresh);
      }
    } catch (err) {
      showToast(err.message || 'Failed to load course', 'error');
    } finally {
      setLoading(false);
    }
  }, [courseId, showToast]);

  useEffect(() => { reload(); }, [reload]);

  const completedSet = useMemo(() => new Set(course?.attempt?.lessonsCompleted || []), [course]);
  const allLessonsDone = useMemo(() => {
    if (!course) return false;
    const lessons = course.lessons || [];
    return lessons.length > 0 && lessons.every((l) => completedSet.has(l.id));
  }, [course, completedSet]);

  const activeLesson = (course?.lessons || [])[activeLessonIdx];

  const handleMarkComplete = async () => {
    if (!activeLesson) return;
    try {
      const updated = await completeLesson(courseId, activeLesson.id);
      setCourse((prev) => ({ ...prev, attempt: updated }));
      // Auto-advance to next incomplete lesson or to quiz
      const lessons = course.lessons || [];
      const nextIdx = lessons.findIndex((l, i) => i > activeLessonIdx && !(updated.lessonsCompleted || []).includes(l.id));
      if (nextIdx >= 0) {
        setActiveLessonIdx(nextIdx);
      } else {
        const incompleteIdx = lessons.findIndex((l) => !(updated.lessonsCompleted || []).includes(l.id));
        if (incompleteIdx >= 0) setActiveLessonIdx(incompleteIdx);
      }
    } catch (err) {
      showToast(err.message || 'Failed to mark complete', 'error');
    }
  };

  const handleStartQuiz = () => {
    setQuizResult(null);
    setAnswers({});
    setView('quiz');
  };

  const handleSubmitQuiz = async () => {
    if (!course) return;
    const questions = course.quiz?.questions || [];
    const missing = questions.filter((q) => answers[q.id] === undefined);
    if (missing.length) {
      showToast(`Please answer all ${questions.length} questions (${missing.length} remaining)`, 'error');
      return;
    }
    setSubmitting(true);
    try {
      const payload = questions.map((q) => ({ questionId: q.id, value: answers[q.id] }));
      const result = await submitQuiz(courseId, payload);
      setQuizResult(result);
      setView('result');
      if (result.passed && result.certificateId) {
        showToast(`You passed! Certificate ${result.certificateId} issued.`, 'success');
      } else if (result.passed) {
        showToast('Passed!', 'success');
      } else {
        showToast(`Score ${result.scorePercent}% · below passing ${result.passPercent}%`, 'info');
      }
    } catch (err) {
      showToast(err.message || 'Submit failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setAnswers({});
    setQuizResult(null);
    setView('quiz');
  };

  if (loading || !course) {
    return (
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }} data-testid="course-player-loading">
        <div style={S.card}>
          <div style={{ color: C.textMid, fontStyle: 'italic' }}>Loading course…</div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="course-player" style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 0' }}>
      {/* Top bar */}
      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            type="button"
            data-testid="course-player-back"
            onClick={onBack}
            style={{ ...S.buttonSecondary, padding: '8px 14px' }}
          >
            <Icon name="arrow-back-outline" style={{ fontSize: 14, marginRight: 4, verticalAlign: '-1px' }} />
            My Training
          </button>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 11, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Course
              {course.attempt?.status === 'passed' ? (
                <span style={{ marginLeft: 8, color: C.green }}>· Completed</span>
              ) : null}
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{course.title}</div>
            {course.description ? (
              <div style={{ fontSize: 13, color: C.textMid, marginTop: 2 }}>{course.description}</div>
            ) : null}
          </div>
          <ProgressRing
            completed={(course.attempt?.lessonsCompleted || []).length}
            total={(course.lessons || []).length}
            passed={course.attempt?.status === 'passed'}
          />
        </div>
      </div>

      {view === 'quiz' && (
        <QuizView
          quiz={course.quiz}
          answers={answers}
          setAnswers={setAnswers}
          submitting={submitting}
          onSubmit={handleSubmitQuiz}
          onCancel={() => setView('lesson')}
        />
      )}

      {view === 'result' && quizResult && (
        <ResultView
          result={quizResult}
          quiz={course.quiz}
          certificateId={quizResult.certificateId}
          onRetry={handleRetry}
          onBackToTraining={onBack}
          onViewCertificate={() => quizResult.certificateId && onCertificateIssued && onCertificateIssued(quizResult.certificateId)}
        />
      )}

      {view === 'lesson' && (
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16, alignItems: 'start' }}>
          {/* Lesson sidebar */}
          <div style={S.card}>
            <div style={{ ...S.heading, marginBottom: 8 }}>Lessons</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {(course.lessons || []).map((l, idx) => {
                const done = completedSet.has(l.id);
                const active = idx === activeLessonIdx;
                return (
                  <button
                    key={l.id}
                    type="button"
                    data-testid={`lesson-tab-${l.id}`}
                    onClick={() => setActiveLessonIdx(idx)}
                    style={{
                      padding: '8px 10px',
                      borderRadius: 6,
                      border: 'none',
                      background: active ? C.accentBg : 'transparent',
                      color: active ? C.accent : (done ? C.green : C.text),
                      fontSize: 13,
                      fontWeight: active ? 700 : 500,
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: "'Inter', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <Icon
                      name={done ? 'checkmark-circle' : 'ellipse-outline'}
                      style={{ fontSize: 16, color: done ? C.green : C.textLight }}
                    />
                    <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {idx + 1}. {l.title}
                    </span>
                  </button>
                );
              })}
              {/* Quiz row */}
              <button
                type="button"
                data-testid="lesson-tab-quiz"
                onClick={() => allLessonsDone ? handleStartQuiz() : showToast('Complete all lessons first', 'info')}
                disabled={!allLessonsDone}
                style={{
                  marginTop: 6,
                  padding: '10px 10px',
                  borderRadius: 6,
                  border: `1px dashed ${allLessonsDone ? C.accent : C.border}`,
                  background: allLessonsDone ? C.accentBg : 'transparent',
                  color: allLessonsDone ? C.accent : C.textLight,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: allLessonsDone ? 'pointer' : 'not-allowed',
                  textAlign: 'left',
                  fontFamily: "'Inter', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Icon name="help-buoy-outline" style={{ fontSize: 16 }} />
                Final Quiz · {course.quiz?.questions?.length || 0} questions
              </button>
            </div>
          </div>

          {/* Lesson content */}
          <div style={S.card}>
            {!activeLesson && (
              <div style={{ color: C.textLight, fontStyle: 'italic' }}>Select a lesson on the left.</div>
            )}
            {activeLesson && (
              <div>
                <div style={{ fontSize: 11, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  Lesson {activeLessonIdx + 1} of {(course.lessons || []).length}
                </div>
                <h2 style={{ margin: '4px 0 16px', fontSize: 22, fontWeight: 800, color: C.text, letterSpacing: '-0.01em' }}>
                  {activeLesson.title}
                </h2>
                {(activeLesson.contentBlocks || []).map((b) => <LessonBlock key={b.id} block={b} />)}
                {(activeLesson.contentBlocks || []).length === 0 && (
                  <div style={{ color: C.textLight, fontStyle: 'italic', fontSize: 13 }}>(empty lesson)</div>
                )}
                {/* Mark complete action */}
                <div style={{ marginTop: 24, paddingTop: 16, borderTop: `1px solid ${C.borderLight}`, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {!completedSet.has(activeLesson.id) ? (
                    <button
                      type="button"
                      data-testid="mark-lesson-complete"
                      onClick={handleMarkComplete}
                      style={S.buttonPrimary}
                    >
                      <Icon name="checkmark-outline" style={{ fontSize: 14, marginRight: 6, verticalAlign: '-1px' }} />
                      Mark lesson as complete
                    </button>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: C.green, fontWeight: 700, fontSize: 13 }}>
                      <Icon name="checkmark-circle" style={{ fontSize: 18 }} />
                      Completed
                    </span>
                  )}
                  {activeLessonIdx < (course.lessons || []).length - 1 && (
                    <button
                      type="button"
                      data-testid="next-lesson"
                      onClick={() => setActiveLessonIdx((i) => i + 1)}
                      style={S.buttonSecondary}
                    >
                      Next lesson →
                    </button>
                  )}
                  {allLessonsDone && (
                    <button
                      type="button"
                      data-testid="go-to-quiz"
                      onClick={handleStartQuiz}
                      style={{ ...S.buttonPrimary, background: C.green, borderColor: C.green, marginLeft: 'auto' }}
                    >
                      Start Final Quiz →
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ProgressRing({ completed, total, passed }) {
  const pct = total ? (completed / total) * 100 : 0;
  const stroke = passed ? C.green : C.accent;
  const r = 26;
  const C_ = 2 * Math.PI * r;
  const offset = C_ * (1 - pct / 100);
  return (
    <div style={{ position: 'relative', width: 62, height: 62 }}>
      <svg width="62" height="62" viewBox="0 0 62 62">
        <circle cx="31" cy="31" r={r} fill="none" stroke={C.borderLight} strokeWidth="5" />
        <circle
          cx="31"
          cy="31"
          r={r}
          fill="none"
          stroke={stroke}
          strokeWidth="5"
          strokeDasharray={C_}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 31 31)"
          style={{ transition: 'stroke-dashoffset 0.3s' }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: stroke, fontFamily: "'JetBrains Mono', monospace" }}>
        {Math.round(pct)}%
      </div>
    </div>
  );
}

function LessonBlock({ block }) {
  if (!block) return null;
  if (block.type === 'richText') {
    return (
      <div className="md-preview" style={{ fontSize: 15, color: C.text, lineHeight: 1.7, marginBottom: 16 }}
        dangerouslySetInnerHTML={{ __html: renderMarkdown(block.text || '') }} />
    );
  }
  if (block.type === 'image' && block.url) {
    return (
      <img src={block.url} alt={block.alt || ''} style={{ maxWidth: '100%', borderRadius: 10, margin: '12px 0', border: `1px solid ${C.borderLight}` }} />
    );
  }
  if (block.type === 'videoEmbed' && block.url) {
    return (
      <div style={{ padding: 14, background: C.bg, border: `1px dashed ${C.border}`, borderRadius: 10, margin: '12px 0' }}>
        <a href={block.url} target="_blank" rel="noreferrer" style={{ color: C.accent, fontWeight: 700, textDecoration: 'none' }}>
          ▶ Watch video
        </a>
        {block.text ? <div style={{ marginTop: 6, fontSize: 13, color: C.textMid }}>{block.text}</div> : null}
      </div>
    );
  }
  if (block.type === 'attachment' && block.url) {
    return (
      <div style={{ margin: '12px 0', fontSize: 14 }}>
        📎 <a href={block.url} target="_blank" rel="noreferrer" style={{ color: C.accent, fontWeight: 600 }}>
          {block.name || block.url}
        </a>
      </div>
    );
  }
  if (block.type === 'callout') {
    const variant = block.variant || 'info';
    const palette = variant === 'success' ? { bg: C.greenLight, fg: C.green }
                  : variant === 'warn'    ? { bg: C.orangeLight, fg: C.orange }
                  :                          { bg: C.accentBg, fg: C.accent };
    return (
      <div style={{ padding: 14, borderRadius: 10, background: palette.bg, border: `1px solid ${palette.fg}33`, margin: '12px 0' }}>
        {block.title && <div style={{ fontWeight: 700, color: palette.fg, marginBottom: 4 }}>{block.title}</div>}
        <div className="md-preview" style={{ fontSize: 14, color: C.text, lineHeight: 1.6 }}
          dangerouslySetInnerHTML={{ __html: renderMarkdown(block.text || '') }} />
      </div>
    );
  }
  if (block.type === 'keyTakeaway') {
    return (
      <div style={{ padding: 14, borderRadius: 10, background: C.accentBg, borderLeft: `4px solid ${C.accent}`, margin: '12px 0' }}>
        <div style={{ fontSize: 11, color: C.accent, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Key Takeaway</div>
        <div style={{ fontSize: 15, color: C.text, marginTop: 4, fontWeight: 600 }}>{block.text}</div>
      </div>
    );
  }
  if (block.type === 'definitionTooltip') {
    return (
      <div style={{ margin: '8px 0', fontSize: 14 }}>
        <strong style={{ color: C.text }}>{block.term}:</strong>{' '}
        <span style={{ color: C.textMid }}>{block.definition}</span>
      </div>
    );
  }
  if (block.type === 'reflectionPrompt') {
    return (
      <div style={{ padding: 14, borderRadius: 10, background: C.purpleLight, borderLeft: `4px solid ${C.purple}`, margin: '12px 0' }}>
        <div style={{ fontSize: 11, color: C.purple, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Reflection</div>
        <div style={{ fontSize: 15, color: C.text, marginTop: 4 }}>{block.text}</div>
      </div>
    );
  }
  return null;
}

function QuizView({ quiz, answers, setAnswers, submitting, onSubmit, onCancel }) {
  const questions = quiz?.questions || [];
  return (
    <div style={S.card} data-testid="quiz-view">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 11, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Final Quiz</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>
            {questions.length} question{questions.length === 1 ? '' : 's'} · Pass at {quiz?.passPercent || 80}%
          </div>
        </div>
        <button type="button" data-testid="quiz-cancel" onClick={onCancel} style={S.buttonSecondary}>
          Back to lessons
        </button>
      </div>
      {questions.map((q, idx) => (
        <div key={q.id} data-testid={`quiz-q-${q.id}`} style={{ marginBottom: 18, padding: 14, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 11, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>Question {idx + 1}</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 10 }}>{q.prompt}</div>
          {q.type === 'multiple_choice' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(q.options || []).map((opt, i) => (
                <label
                  key={i}
                  data-testid={`quiz-q-${q.id}-opt-${i}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: `1px solid ${answers[q.id] === i ? C.accent : C.border}`,
                    background: answers[q.id] === i ? C.accentBg : '#fff',
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  <input
                    type="radio"
                    name={`q-${q.id}`}
                    checked={answers[q.id] === i}
                    onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: i }))}
                  />
                  {opt}
                </label>
              ))}
            </div>
          )}
          {q.type === 'true_false' && (
            <div style={{ display: 'flex', gap: 10 }}>
              {[true, false].map((v) => (
                <label
                  key={String(v)}
                  data-testid={`quiz-q-${q.id}-${v ? 'true' : 'false'}`}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    padding: '14px 12px',
                    borderRadius: 8,
                    border: `1px solid ${answers[q.id] === v ? C.accent : C.border}`,
                    background: answers[q.id] === v ? C.accentBg : '#fff',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  <input type="radio" name={`q-${q.id}`} checked={answers[q.id] === v} onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: v }))} />
                  {v ? 'True' : 'False'}
                </label>
              ))}
            </div>
          )}
        </div>
      ))}
      <button
        type="button"
        data-testid="quiz-submit"
        onClick={onSubmit}
        disabled={submitting}
        style={{ ...S.buttonPrimary, opacity: submitting ? 0.6 : 1, padding: '12px 24px', fontSize: 14 }}
      >
        {submitting ? 'Submitting…' : 'Submit Quiz'}
      </button>
    </div>
  );
}

function ResultView({ result, quiz, certificateId, onRetry, onBackToTraining, onViewCertificate }) {
  const passed = result.passed;
  const showBreakdown = quiz?.reviewAnswers !== false && Array.isArray(result.breakdown) && result.breakdown.length > 0;
  return (
    <div data-testid="quiz-result" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: passed ? C.greenLight : C.redLight,
            color: passed ? C.green : C.red,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 32,
            border: `3px solid ${passed ? C.green : C.red}`,
          }}>
            <Icon name={passed ? 'checkmark' : 'close'} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 11, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {passed ? 'Congratulations' : 'Not quite there'}
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: passed ? C.green : C.red, letterSpacing: '-0.02em' }}>
              {result.scorePercent}% — {passed ? 'Passed' : 'Try again'}
            </div>
            <div style={{ fontSize: 13, color: C.textMid, marginTop: 2 }}>
              {result.correctCount} of {result.totalQuestions} correct · Pass threshold {result.passPercent}%
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {passed && certificateId && (
              <button type="button" data-testid="view-certificate" onClick={onViewCertificate} style={S.buttonPrimary}>
                <Icon name="ribbon-outline" style={{ fontSize: 14, marginRight: 6, verticalAlign: '-1px' }} />
                View certificate
              </button>
            )}
            {!passed && (
              <button type="button" data-testid="retry-quiz" onClick={onRetry} style={S.buttonPrimary}>
                Retry quiz
              </button>
            )}
            <button type="button" data-testid="back-to-training" onClick={onBackToTraining} style={S.buttonSecondary}>
              Back to training
            </button>
          </div>
        </div>
      </div>

      {showBreakdown && (
        <div style={S.card}>
          <div style={{ ...S.heading, marginBottom: 10 }}>Review your answers</div>
          {result.breakdown.map((b, i) => (
            <div key={b.questionId} style={{ padding: 12, borderTop: i > 0 ? `1px solid ${C.borderLight}` : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <Icon
                  name={b.correct ? 'checkmark-circle' : 'close-circle'}
                  style={{ fontSize: 20, color: b.correct ? C.green : C.red, marginTop: 2 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{b.prompt}</div>
                  {b.explanation && (
                    <div style={{ marginTop: 6, padding: 10, background: C.bg, borderRadius: 8, fontSize: 13, color: C.textMid }}>
                      <strong>Why:</strong> {b.explanation}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
